import React from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import resourceStep1 from '../../../assets/images/adminSetupGuide/resourceStep1.png';
import resourceStep2 from '../../../assets/images/adminSetupGuide/resourceStep2.png';
import resourceStep3 from '../../../assets/images/adminSetupGuide/resourceStep3.png';
import resourceStep4 from '../../../assets/images/adminSetupGuide/resourceStep4.png';
import resourceStep5 from '../../../assets/images/adminSetupGuide/resourceStep5.png';
import {buildSettingsAppsTabUrl} from '../../../models/url/settingsUrlHelpers';
import {SettingsAppsTabsEnum} from '../../../models/url/settingsUrlModel';
import {AdminSetupGuideResourceTaskDefinition} from './adminSetupGuideTypes';

/*
 * Constants.
 */

export const resourceTaskDefinitions: ReadonlyArray<AdminSetupGuideResourceTaskDefinition> = [
  {
    key: 'eliminate-missed-connections',
    title: (
      <Trans id="admin-setup-guide-more-screen-missed-connections-title">
        Eliminate missed customer communication
      </Trans>
    ),
    image: resourceStep1,
    resources: [
      {
        key: 'sla-rules',
        title: <Trans id="admin-setup-guide-more-screen-missed-connections-sla-rules">SLA rules</Trans>,
        displayName: 'SLA rules',
        icon: 'timeSmall',
        link: 'https://academy.front.com/app/courses/8d72797f-97bd-453f-b923-d4328aacf09d',
      },
      {
        key: 'assignment-routing',
        title: (
          <Trans id="admin-setup-guide-more-screen-missed-connections-assignment-routing">
            Assignment &amp; Routing
          </Trans>
        ),
        displayName: 'Assignment & Routing',
        icon: 'assignActivity',
        link: 'https://academy.front.com/app/courses/e2b3e900-822e-4c03-90a1-2e1b806993db',
      },
    ],
  },
  {
    key: 'prioritize-workload',
    title: (
      <Trans id="admin-setup-guide-more-screen-prioritize-title">Prioritize workload and reduce volume</Trans>
    ),
    image: resourceStep2,
    resources: [
      {
        key: 'categorize',
        title: <Trans id="admin-setup-guide-more-screen-prioritize-tags">Categorize with tags</Trans>,
        displayName: 'Categorize with tags',
        icon: 'tagSmall',
        link: 'https://academy.front.com/app/courses/0de70116-c261-486a-a13b-a0ee43917859',
      },
      {
        key: 'front-chat',
        title: <Trans id="admin-setup-guide-more-screen-prioritize-chat">Front Chat</Trans>,
        displayName: 'Front Chat',
        icon: 'channelChatOutlineSmall',
        link: 'https://academy.front.com/app/courses/a0c8180b-18f4-4511-9242-c9f6614bd000',
      },
      {
        key: 'front-knowledge-base',
        title: <Trans id="admin-setup-guide-more-screen-prioritize-kb">Front Knowledge Base</Trans>,
        displayName: 'Front Knowledge Base',
        icon: 'knowledgeBaseSmall',
        link: 'https://academy.front.com/app/courses/5579ed60-91a7-470a-8030-163e42fce117',
      },
    ],
  },
  {
    key: 'accountability-visibility',
    title: (
      <Trans id="admin-setup-guide-more-screen-accountability-title">
        Better accountability and visibility
      </Trans>
    ),
    image: resourceStep3,
    resources: [
      {
        key: 'front-analytics',
        title: <Trans id="admin-setup-guide-more-screen-accountability-analytics">Front analytics</Trans>,
        displayName: 'Front analytics',
        icon: 'analyticsSmall',
        link: 'https://academy.front.com/app/courses/d2040e07-0d87-4fad-ac4c-75dff2f7402c',
      },
      {
        key: 'route-conversations',
        title: (
          <Trans id="admin-setup-guide-more-screen-accountability-route">
            Route conversations automatically
          </Trans>
        ),
        displayName: 'Route conversations automatically',
        icon: 'moveRightSmall',
        link: 'https://academy.front.com/app/courses/38130ea5-89da-4e7b-95cf-563eb1331faf',
      },
    ],
  },
  {
    key: 'consolidate-channels',
    title: (
      <Trans id="admin-setup-guide-more-screen-consolidate-title">
        Consolidate channels and tools into a single platform
      </Trans>
    ),
    image: resourceStep4,
    resources: [
      {
        key: 'connect-apps',
        title: <Trans id="admin-setup-guide-more-screen-consolidate-apps">Third-party apps</Trans>,
        displayName: 'Third-party apps',
        icon: 'integrationsSmall',
        link: 'https://academy.front.com/app/courses/552365d8-e147-4747-b431-d27a0d7d66fe',
      },
      {
        key: 'non-email-channels',
        title: (
          <Trans id="admin-setup-guide-more-screen-consolidate-non-email-channels">Non-email channels</Trans>
        ),
        displayName: 'Non-email channels',
        icon: 'channelsSmall',
        link: buildSettingsAppsTabUrl(SettingsAppsTabsEnum.CHANNELS),
        isInternal: true,
      },
      {
        key: 'dynamic-objects',
        title: (
          <Trans id="admin-setup-guide-more-screen-consolidate-dynamic-objects">
            Dynamic objects (e.g. orders)
          </Trans>
        ),
        displayName: 'Dynamic objects (e.g. orders)',
        icon: 'link',
        link: 'https://academy.front.com/app/courses/5229a341-304e-40f0-bf33-f14409b25022',
      },
    ],
  },
  {
    key: 'accounts-contacts',
    title: (
      <Trans id="admin-setup-guide-more-screen-crm-context-">
        Leverage CRM information for context and automation
      </Trans>
    ),
    image: resourceStep5,
    resources: [
      {
        key: 'accounts-contacts',
        title: (
          <Trans id="admin-setup-guide-more-screen-crm-context-accounts-contacts">
            Accounts and contacts
          </Trans>
        ),
        displayName: 'Accounts and contacts',
        icon: 'briefcaseOutlineSmall',
        link: 'https://academy.front.com/app/courses/316e96db-be5f-44f0-aff9-e0bc8953a986',
      },
      {
        key: 'crm-sync',
        title: <Trans id="admin-setup-guide-more-screen-crm-context-sync">CRM sync</Trans>,
        displayName: 'CRM sync',
        icon: 'teammatesSmall',
        link: 'https://academy.front.com/courses/316e96db-be5f-44f0-aff9-e0bc8953a986/activities/8361e394-6ed6-4138-af92-c77586124a50',
      },
      {
        key: 'account-based-rules',
        title: (
          <Trans id="admin-setup-guide-more-screen-crm-context-account-based-rules">
            Account based rules
          </Trans>
        ),
        displayName: 'Account based rules',
        icon: 'gear',
        link: 'https://academy.front.com/courses/316e96db-be5f-44f0-aff9-e0bc8953a986/activities/7ddf5f6a-1ce9-4ef5-bcec-4ff409b80194',
      },
    ],
  },
];
